/**
 * The realtime wire contract (TO-468, ADR-0049): what notification-worker
 * publishes to IoT and what the browser accepts. A message is an INVALIDATION
 * HINT — a pointer (`entityRef`), never content — so consumers refetch rather
 * than write the payload into a cache.
 */

/** The entity a realtime message points at. */
export interface RealtimeEntityRef {
	type: string
	id: string
}

/**
 * The public wire message. A projection of the bus envelope that drops the
 * internal `eventId`, `version` and `source`.
 */
export interface RealtimeMessage {
	/** An `EventType` value from `REALTIME_PUSH_EVENT_TYPES`. */
	type: string
	tenantId: string
	userId: string
	entityRef: RealtimeEntityRef
	/** A hint only; read defensively. */
	data?: Record<string, unknown>
	/** ISO-8601 UTC. */
	timestamp: string
	correlationId?: string
}

const isString = (v: unknown): v is string => typeof v === 'string'
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
	typeof v === 'object' && v !== null && !Array.isArray(v)

/** Structural guard for an incoming payload (after `JSON.parse`). */
export function isRealtimeMessage(value: unknown): value is RealtimeMessage {
	if (!isPlainObject(value)) return false
	const { type, tenantId, userId, timestamp, entityRef, data, correlationId } = value
	if (!isString(type) || !isString(tenantId) || !isString(userId) || !isString(timestamp)) return false
	if (!isPlainObject(entityRef) || !isString(entityRef.type) || !isString(entityRef.id)) return false
	if (data !== undefined && !isPlainObject(data)) return false
	if (correlationId !== undefined && !isString(correlationId)) return false
	return true
}
